import { create } from 'zustand';

export type TripPurpose = string;

export interface LogbookFormState {
    // Form data
    selectedCarId: string;
    date: string;
    purpose: TripPurpose | null;
    rateType: string;
    kilometers: number;
    deposit: number;
    cardPayment: number;
    additionalExpense: number;
    additionalExpenseType: string;
    notes: string;
    previouslyAgreedPrice: number;
    personalWriteoff: number;
    companyWriteoff: number;
    generalWriteoff: number;

    // UI state
    isSubmitting: boolean;
    submitError: string | null;
    submitSuccess: boolean;

    // Calculations (derived state)
    totalPrice: number;
    netIncome: number;

    // Actions
    setSelectedCarId: (carId: string) => void;
    setDate: (date: string) => void;
    setPurpose: (purpose: TripPurpose | null) => void;
    setRateType: (rateType: string) => void;
    setKilometers: (km: number) => void;
    setDeposit: (deposit: number) => void;
    setCardPayment: (payment: number) => void;
    setAdditionalExpense: (expense: number) => void;
    setAdditionalExpenseType: (type: string) => void;
    setNotes: (notes: string) => void;
    setPreviouslyAgreedPrice: (price: number) => void;
    setPersonalWriteoff: (km: number) => void;
    setCompanyWriteoff: (km: number) => void;
    setGeneralWriteoff: (km: number) => void;
    setIsSubmitting: (submitting: boolean) => void;
    setSubmitError: (error: string | null) => void;
    setSubmitSuccess: (success: boolean) => void;

    // Bulk operations
    updateFormData: (data: Partial<LogbookFormState>) => void;
    calculatePricing: (rate: number) => void;
    resetForm: () => void;
}

const initialState = {
    selectedCarId: '',
    date: new Date().toISOString().split('T')[0],
    purpose: null as TripPurpose | null,
    rateType: '',
    kilometers: 0,
    deposit: 0,
    cardPayment: 0,
    additionalExpense: 0,
    additionalExpenseType: '',
    notes: '',
    previouslyAgreedPrice: 0,
    personalWriteoff: 5,
    companyWriteoff: 0,
    generalWriteoff: 0,
    isSubmitting: false,
    submitError: null,
    submitSuccess: false,
    totalPrice: 0,
    netIncome: 0,
};

export const useLogbookStore = create<LogbookFormState>((set) => ({
    ...initialState,

    setSelectedCarId: (carId: string) =>
        set({ selectedCarId: carId, submitError: null }),

    setDate: (date: string) => set({ date, submitError: null }),

    setPurpose: (purpose: TripPurpose | null) =>
        set({ purpose, submitError: null }),

    setRateType: (rateType: string) =>
        set({ rateType, submitError: null }),

    setKilometers: (kilometers: number) =>
        set({ kilometers: Math.max(0, kilometers), submitError: null }),

    setDeposit: (deposit: number) =>
        set({ deposit: Math.max(0, deposit), submitError: null }),

    setCardPayment: (cardPayment: number) =>
        set({ cardPayment: Math.max(0, cardPayment), submitError: null }),

    setAdditionalExpense: (additionalExpense: number) =>
        set({ additionalExpense: Math.max(0, additionalExpense), submitError: null }),

    setAdditionalExpenseType: (additionalExpenseType: string) =>
        set({ additionalExpenseType, submitError: null }),

    setNotes: (notes: string) => set({ notes, submitError: null }),

    setPreviouslyAgreedPrice: (previouslyAgreedPrice: number) =>
        set({ previouslyAgreedPrice, submitError: null }),

    setPersonalWriteoff: (personalWriteoff: number) =>
        set({ personalWriteoff: Math.max(0, personalWriteoff), submitError: null }),

    setCompanyWriteoff: (companyWriteoff: number) =>
        set({ companyWriteoff: Math.max(0, companyWriteoff), submitError: null }),

    setGeneralWriteoff: (generalWriteoff: number) =>
        set({ generalWriteoff: Math.max(0, generalWriteoff), submitError: null }),

    setIsSubmitting: (isSubmitting: boolean) => set({ isSubmitting }),

    setSubmitError: (submitError: string | null) => set({ submitError }),

    setSubmitSuccess: (submitSuccess: boolean) =>
        set({ submitSuccess, submitError: null }),

    updateFormData: (data: Partial<LogbookFormState>) =>
        set((state) => ({
            ...state,
            ...data,
            submitError: null,
        })),

    calculatePricing: (rate: number) =>
        set((state) => {
            const totalPrice = state.kilometers * rate;
            const netIncome = totalPrice - state.deposit - state.cardPayment - state.additionalExpense;
            return { totalPrice, netIncome };
        }),

    resetForm: () => set(initialState),
}));
